/**
 * SimilarPlayersFilterPanelView - Filter panel wrapper for similar players
 *
 * On desktop: wraps filter content in a ScrollView within a bordered sidebar.
 * On mobile (isFullWidth): wraps content in a plain VStack so the parent
 * FullScreenOverlay's ScrollView handles scrolling, avoiding nested-ScrollView
 * gesture conflicts that block all scrolling when the leagues dropdown opens.
 * @remarks This is a View component - contains no business logic, hooks, or state
 */

import React, { memo } from "react";
import { ScrollView } from "@/components/atoms/ScrollView";

import {
  SortFieldCallbackMap,
  SimilarPlayersFilterData,
  SimilarPlayersFilterCallbacks,
} from "@/features/player-detail/types";
import type { OptionPickerProps } from "@/features/player-detail/utils/similarPlayersQueryState";
import type {
  RosterManagementLeagueFragment,
  RosterManagementNationalityFragment,
} from "@/generated/graphql";
import SimilarPlayersFilterContent from "@/features/player-detail/components/organisms/SimilarPlayersFilterContent";

// design-system: compound waiver — non-collapsible button composition
// eslint-disable-next-line no-restricted-imports -- RFC-#6 genuine-runtime: desktop sidebar caps height at a fixed off-scale maxHeight:600 (no 600px sizing token; max-h ladder tops out at 96=384px) on…
import { Button, ButtonText } from "@/components/ui/button";
import { Stack } from "@/components/atoms/Stack";

/**
 * Props for SimilarPlayersFilterPanelView component
 */
interface SimilarPlayersFilterPanelViewProps
  extends
    Omit<SimilarPlayersFilterData, "leaguesState" | "nationalitiesState">,
    SimilarPlayersFilterCallbacks {
  /** Leagues picker props, flattened by the container (SE-7580) */
  readonly leaguesPicker: OptionPickerProps<RosterManagementLeagueFragment>;
  /** Nationalities picker props, flattened by the container (SE-7580) */
  readonly nationalitiesPicker: OptionPickerProps<RosterManagementNationalityFragment>;
  /** Whether dark mode is active */
  readonly isDark: boolean;
  /** Whether any filter has pending uncommitted changes requiring save */
  readonly hasPendingChanges: boolean;
  /** Whether filters are at their default values */
  readonly isDefaultFilters: boolean;
  /** Callback when Save button is pressed */
  readonly onSaveFilters: () => void;
  /** Callback when Reset button is pressed */
  readonly onResetFilters: () => void;
  /** Pre-bound callbacks for each sort field to avoid inline function creation */
  readonly sortFieldCallbacks: SortFieldCallbackMap;
  /** Whether to render at full width without border (for modal contexts) */
  readonly isFullWidth?: boolean;
}

/**
 * Renders the filter panel wrapper with conditional scroll behavior.
 *
 * The wrapper only consumes a handful of props directly (layout + footer
 * actions); everything the filter sections need is forwarded as `...contentProps`
 * so the desktop (ScrollView) and mobile (full-width) branches share one prop
 * list instead of duplicating it.
 * @param props - Component props
 * @param props.isDark - Whether dark mode is active
 * @param props.hasPendingChanges - Whether any filter has pending changes
 * @param props.isDefaultFilters - Whether filters are at default values
 * @param props.onSaveFilters - Callback when Save button is pressed
 * @param props.onResetFilters - Callback when Reset button is pressed
 * @param props.isFullWidth - Whether to render at full width without border
 * @param props.contentProps - Remaining props forwarded to the filter sections
 * @returns Filter panel VStack
 */
const SimilarPlayersFilterPanelView = ({
  isDark,
  hasPendingChanges,
  isDefaultFilters,
  onSaveFilters,
  onResetFilters,
  isFullWidth,
  ...contentProps
}: SimilarPlayersFilterPanelViewProps) => (
  <Stack
    className={
      isFullWidth
        ? "flex-1"
        : "w-80 rounded-lg border border-outline-card bg-surface-card"
    }
    // eslint-disable-next-line no-restricted-syntax -- fixed 600px desktop sidebar height cap; off the sizing ladder (max-h tops out at 96=384px)
    UNSAFE_style={isFullWidth ? undefined : { maxHeight: 600 }}
  >
    {isFullWidth ? (
      <Stack className="flex-1 p-4">
        <SimilarPlayersFilterContent
          {...contentProps}
          isDark={isDark}
          isFullWidth
        />
      </Stack>
    ) : (
      <ScrollView className="flex-1" contentContainerClassName="p-4">
        <SimilarPlayersFilterContent {...contentProps} isDark={isDark} />
      </ScrollView>
    )}

    {/* Action Buttons - sticky footer outside ScrollView */}
    {(hasPendingChanges || !isDefaultFilters) && (
      <Stack
        direction="horizontal"
        className="border-border-outline-subtle justify-end gap-2 border-t px-4 py-3"
      >
        {!isDefaultFilters && (
          <Button
            variant="link"
            size="sm"
            onPress={onResetFilters}
            className="px-4"
          >
            <ButtonText className="text-content-muted">Reset</ButtonText>
          </Button>
        )}
        {hasPendingChanges && (
          <Button
            variant="solid"
            size="sm"
            onPress={onSaveFilters}
            className="bg-needs-semantic-primary-500 px-6"
          >
            <ButtonText className="text-content-inverse">Save</ButtonText>
          </Button>
        )}
      </Stack>
    )}
  </Stack>
);

SimilarPlayersFilterPanelView.displayName = "SimilarPlayersFilterPanelView";

export default memo(SimilarPlayersFilterPanelView);
